/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
// For dynamic theme modifications
import { unstable_createMuiStrictModeTheme as createMuiTheme, ThemeProvider } from '@mui/material/styles';

//import { createTheme, ThemeProvider } from '@mui/material/styles';

import { useSelector } from 'react-redux';

import * as Q from '../../../../../../constants/graphqlQueries';
import CustomDropdown from '../../../../../components/Dropdown/CustomDropdown';
import CustomTextField from '../../../../../components/Textfield/CustomTextField';
import CustomButtons from '../../../../../components/Button/CustomButtons';
import colors from '../../../../../../constants/colors';
import UserRoles from '../../../../../../constants/userRoles';

const theme = createMuiTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
        },
      },
    },
  },
});
const useStyles = makeStyles(() => ({
  container: {
    margin: '0 14px',
    paddingBottom: '4px',
  },
  title: {
    fontFamily: 'Segoe UI',
    fontSize: '1.6rem',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    color: colors.blue,
    marginTop: '8px',
    marginBottom: '4px',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    paddingBottom: '6px',
  },
  rowFooter: {
    display: 'flex',
    marginBottom: '4px',
    marginTop: '8px',
    justifyContent: 'center',
    gap: '8px',
  },
}));
const $ALL = 'All';
const FieldIndex = {
  client: 1,
  program: 2,
  project: 3,
  site: 4
}

const activeFilterOptions = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

const Search = ({ onSearchClick, isDisabled, onClearSearch, onActiveFilterChange }) => {
  const classes = useStyles('');
  const user = useSelector((state) => state.User);
  const [selectedClient, setSelectedClient] = useState();
  const [selectedProgram, setSelectedProgram] = useState();
  const [selectedProject, setSelectedProject] = useState();
  const [selectedSite, setSelectedSite] = useState();
  const [ruleName, setRuleName] = useState('');
  const [selectedActive, setSelectedActive] = useState(null);

  const [clients, setClients] = useState({});
  const [programs, setPrograms] = useState({});
  const [projects, setProjects] = useState({});
  const [sites, setSites] = useState({});
  const [disabledButton, setDisabledButton] = useState(false);
  const [inputFieldDisabled, setInputFieldDisabled] = useState(0);

  const [clientParentIdentifiers, setClientParentIdentifiers] = useState(null);

  // lazy queries
  const [getPrograms, { data: dataPrograms }] = useLazyQuery(Q.APP_ALERT_PROGRAMS_GL_QUERY);
  const [getProjects, { data: dataProjects }] = useLazyQuery(Q.APP_ALERT_PROJECTS_GL_QUERY);

  const { data: dataClients } = useQuery(Q.APP_ALERT_CLIENTS_GL_QUERY);
  const { data: dataSites } = useQuery(Q.APP_ALERT_SITES_GL_QUERY);

  // Get user Client Parent Access
  const { data: ClientParentAccess } = useQuery(Q.ADMIN_MANAGMENT_SEARCH_CLIENT_PARENT_ACCESS, { variables: { EmployeeId: `${user.EmployeeId}` } });

  const activeDropdownProps = {
    options: activeFilterOptions,
    getOptionLabel: (option) => option.label,
  };

  const onClearCPPS = (element) => {
    if (element === 4) {
      setSelectedClient(null);
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
    }
    else if (element === 3) {
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
    } 
    else if ( element === 2) {
      setSelectedProject(null);
      setSelectedSite(null);
    } 
    else {
      setSelectedSite(null);
    }
  }

  const onClearClick = () => {
    onClearCPPS(4);
    setRuleName('');
    setSelectedActive(null);
    setDisabledButton(!isDisabled)
    onClearSearch();
  }

  const onChangeClient = (client) => {
    if (client) {
      setSelectedClient(client);
      onClearCPPS(3);

      getPrograms({ variables: { ClientId: client.ClientId } });
    }
    else {
      onClearCPPS(4);
    }
  }

  const onChangeProgram = (program) => {
    if (program) {
      setSelectedProgram(program);
      onClearCPPS(2);

      getProjects({ variables: { ProgramId: program.ProgramId } });
    }
    else {
      onClearCPPS(3);
    }
  }

  const onChangeProject = (project) => {
    if (project) {
      setSelectedProject(project);
      onClearCPPS(1);
    }
    else {
      onClearCPPS(2);
    }
  }

  const onChangeSite = (site) => {
    if (site) {
      setSelectedSite(site);
    }
    else {
      onClearCPPS(1);
    }
  }

  const onChangeRuleName = (value) => {
    setRuleName(value);
  }

  const onChangeActive = (value) => {
    setSelectedActive(value);
    // Immediately trigger client-side filtering when active filter changes
    if (onActiveFilterChange) {
      const activeValue = value ? value.id : null;
      onActiveFilterChange(activeValue);
    }
  }

  const handleSearch = () => {
    onSearchClick(selectedClient, selectedProgram, selectedProject, selectedSite, ruleName);
  }

  // Field identifiers to be used for filtering down the search fields options
  useEffect(() => {
    if (ClientParentAccess?.getWFMClientParentAccess) {
      const clientsId = ClientParentAccess.getWFMClientParentAccess.map(clientParent => clientParent.ClientCode);

      setClientParentIdentifiers({
        clients: clientsId
      });
    }
  }, [ClientParentAccess]);

  // Fill client search field — load immediately when data arrives; apply WFM filter only if parent access is available
  useEffect(() => {
    if (dataClients?.getClients) {
      let clientsOptions = [{ ClientId: $ALL, DescriptionShort: $ALL }, ...dataClients.getClients];

      if (user.Role === UserRoles.WFM && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner && clientParentIdentifiers) {
        clientsOptions = dataClients.getClients.filter(client => clientParentIdentifiers.clients.includes(client.ClientId));
      }

      setClients({
        options: clientsOptions,
        getOptionLabel: (option) => option.ClientId === $ALL ? option.DescriptionShort : `${option.DescriptionShort} (${option.ClientId})`
      });
    }
  }, [dataClients, clientParentIdentifiers]);

  // Fill program search field
  useEffect(() => {
    if (dataPrograms?.getPrograms) {
      const programsOptions = [{ ProgramId: $ALL, DescriptionShort: $ALL }, ...dataPrograms.getPrograms];

      const programProps = {
        options: programsOptions,
        getOptionLabel: (option) => option.ProgramId === $ALL ? option.DescriptionShort : `${option.DescriptionShort} (${option.ProgramId})`
      }
      setPrograms(programProps);
    }
  }, [dataPrograms]);

  // Fill project search field
  useEffect(() => {
    if (dataProjects?.getProjects) {
      const projectsOptions = [{ ProjectId: $ALL, DescriptionShort: $ALL }, ...dataProjects.getProjects];

      const projectProps = {
        options: projectsOptions,
        getOptionLabel: (option) => option.ProjectId === $ALL ? option.DescriptionShort : `${option.DescriptionShort} (${option.ProjectId})`
      }
      setProjects(projectProps);
    }
  }, [dataProjects]);

  // Fill site search field
  useEffect(() => {
    if (dataSites) {
      if (dataSites.getSites) {
        const sitesOptions = [{ SiteId: $ALL, DescriptionShort: $ALL }, ...dataSites.getSites];
        const siteProps = {
          options: sitesOptions,
          getOptionLabel: (option) => option.SiteId === $ALL ? option.DescriptionShort : `${option.DescriptionShort} (${option.SiteId})`
        }

        setSites(siteProps);
      }
    }
  }, [dataSites]);

  useEffect(() => {
    // State watcher
    if (!selectedClient) {
      setInputFieldDisabled(FieldIndex.client);
    }
    else if (!selectedProgram) {
      setInputFieldDisabled(FieldIndex.program);
    }
    else if (!selectedProject) {
      setInputFieldDisabled(FieldIndex.project);
    }
    else if (!selectedSite) {
      setInputFieldDisabled(FieldIndex.site);
    }
    else {
      setInputFieldDisabled(null);
    }
  }, [selectedClient, selectedProgram, selectedProject, selectedSite]);
  useEffect(() => {
    if (!isDisabled) {
      if (inputFieldDisabled === null) {
        setDisabledButton(false);
      }
      else {
        setDisabledButton(true);
      }
    }
  }, [inputFieldDisabled, isDisabled]);
  useEffect(() => {
    if (!isDisabled) {
      setDisabledButton(true);
    } else {
      onClearCPPS(4);
      setDisabledButton(false);
    }
  }, [isDisabled]);

  const isInputFieldDisabled = (inputIndex) => {
    if (inputFieldDisabled && inputIndex > inputFieldDisabled) {
      return true;
    } 
    return false;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className={classes.container}>
        <Box className={classes.title}>
          Search
        </Box>
        <CustomTextField
          title='Rule Name'
          value={ruleName}
          handleChange={onChangeRuleName}
          placeholder='Filter by rule name'
          inputIcon
        />
        <CustomDropdown
          title='Rule Active'
          value={selectedActive}
          handleChange={onChangeActive}
          defaultProps={activeDropdownProps}
          selectedLabel='All'
        />
        <CustomDropdown
          title='Client'
          value={selectedClient}
          handleChange={onChangeClient}
          defaultProps={clients}
          disabled={isDisabled}
          selectedLabel={$ALL}
          mandatory
        />
        <CustomDropdown
          title='Program'
          value={selectedProgram}
          handleChange={onChangeProgram}
          defaultProps={programs}
          disabled={isInputFieldDisabled(FieldIndex.program) || isDisabled}
          selectedLabel={$ALL}
          mandatory
        />
        <CustomDropdown
          title='Project'
          value={selectedProject}
          handleChange={onChangeProject}
          defaultProps={projects}
          disabled={isInputFieldDisabled(FieldIndex.project) || isDisabled}
          selectedLabel={$ALL}
          mandatory
        />
        <CustomDropdown
          title='Site'
          value={selectedSite}
          handleChange={onChangeSite}
          defaultProps={sites}
          disabled={isInputFieldDisabled(FieldIndex.site) || isDisabled}
          selectedLabel={$ALL}
          mandatory
        />
        <Box className={classes.rowFooter}>
          <CustomButtons
            handleClick={onClearClick}
            name='Clear'
          />
          <CustomButtons
            handleClick={handleSearch}
            name='Search'
            disabled={disabledButton}
            tooltipFree
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

Search.propTypes = {
  onSearchClick: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onActiveFilterChange: PropTypes.func,
}

Search.defaultProps = {
  onActiveFilterChange: null,
}

export default Search;
